<script setup lang="ts">
const route = useRoute();
const siteUrl = 'https://www.qiksolve.com';
const defaultOgImage = `${siteUrl}/images/qiksolve_cropped-logo.png`;

const { data: post } = await useAsyncData(route.path, () => queryCollection('blog').path(route.path).first());

if (!post.value) {
  throw createError({ statusCode: 404, statusMessage: 'Blog post not found' });
}

const canonicalUrl = computed(() => {
  const canonicalPath = route.path === '/' ? '/' : `${route.path.replace(/\/$/, '')}/`;
  return `${siteUrl}${canonicalPath}`;
});
const ogImageUrl = computed(() => new globalThis.URL(post.value?.heroImage || defaultOgImage, siteUrl).href);

useSeoMeta({
  title: () => `${post.value?.seoTitle ?? post.value?.title} — QikSolve`,
  description: () => post.value?.description,
  ogTitle: () => post.value?.seoTitle ?? post.value?.title,
  ogDescription: () => post.value?.description,
  ogType: 'article',
  ogUrl: () => canonicalUrl.value,
  ogImage: () => ogImageUrl.value,
  robots: () => post.value?.draft ? 'noindex, nofollow' : 'index, follow',
  twitterCard: () => ogImageUrl.value ? 'summary_large_image' : 'summary',
  twitterTitle: () => post.value?.seoTitle ?? post.value?.title,
  twitterDescription: () => post.value?.description,
  twitterImage: () => ogImageUrl.value,
  articlePublishedTime: () => post.value?.date,
  articleModifiedTime: () => post.value?.updated,
  articleAuthor: () => (post.value?.author ? [post.value.author] : undefined),
});

useHead(() => ({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.value?.title,
        description: post.value?.description,
        image: [ogImageUrl.value],
        datePublished: post.value?.date || undefined,
        dateModified: post.value?.updated || post.value?.date || undefined,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonicalUrl.value,
        },
        author: {
          '@type': 'Organization',
          name: post.value?.author ?? 'QikSolve',
          url: siteUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: 'QikSolve Pty Ltd',
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/images/qiksolve_cropped-logo.png`,
          },
        },
      }),
    },
  ],
}));

const pathwayLinks = [
  {
    label: 'Regulatory compliance',
    description: 'Translate expectations into workable quality practice.',
    to: '/product/regulatory-compliance',
  },
  {
    label: 'Quality systems',
    description: 'Connect records, ownership, review, and improvement.',
    to: '/product/quality-systems',
  },
  {
    label: 'SharePoint governance',
    description: 'Strengthen controlled information and permissions.',
    to: '/product/sharepoint-governance',
  },
  {
    label: 'Governed AI',
    description: 'Explore boundaries, evidence, oversight, and change.',
    to: '/product/agentic-ai-governance',
  },
];
</script>

<template>
  <div class="bg-slate-50">
    <header class="relative isolate overflow-hidden border-b border-brand-100 bg-brand-50/70">
      <template v-if="post?.heroImage">
        <img
          :src="post.heroImage"
          alt=""
          aria-hidden="true"
          width="1200"
          height="900"
          fetchpriority="high"
          class="absolute inset-0 -z-20 h-full w-full object-cover object-right"
        >
        <div
          aria-hidden="true"
          class="absolute inset-0 -z-10 bg-white/80 lg:bg-linear-to-r lg:from-white/85 lg:via-white/60 lg:to-transparent"
        />
      </template>
      <div class="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-18 lg:px-8 lg:py-22">
        <div class="max-w-4xl">
          <p class="text-xs font-bold uppercase tracking-[0.16em] text-brand-700">
            {{ post?.category ?? 'QikSolve insight' }}
          </p>
          <h1 class="mt-5 max-w-4xl text-4xl font-bold leading-tight tracking-tight text-brand-900 sm:text-5xl lg:text-6xl">
            {{ post?.title }}
          </h1>
          <p
            v-if="post?.description"
            class="mt-6 max-w-3xl text-lg leading-8 text-slate-600 sm:text-xl"
          >
            {{ post.description }}
          </p>
          <div class="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-slate-600">
            <span v-if="post?.date">Published {{ post.date }}</span>
            <span v-if="post?.updated">Updated {{ post.updated }}</span>
            <span
              v-if="post?.date && post?.author"
              class="text-brand-300"
              aria-hidden="true"
            >
              /
            </span>
            <span v-if="post?.author">{{ post.author }}</span>
          </div>
        </div>
      </div>
    </header>

    <div class="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[minmax(0,1fr)_18rem] lg:px-8 lg:py-20">
      <main class="min-w-0">
        <div class="rounded-lg border border-slate-200 bg-white px-5 py-8 shadow-[var(--shadow-card)] sm:px-10 sm:py-12 lg:px-14">
          <ContentRenderer
            v-if="post"
            :value="post"
            class="prose prose-slate max-w-none prose-headings:font-heading prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-brand-900 prose-h2:mt-14 prose-h2:border-t prose-h2:border-brand-100 prose-h2:pt-8 prose-h3:text-brand-800 prose-a:font-semibold prose-a:text-brand-700 prose-a:underline prose-a:decoration-brand-200 prose-a:underline-offset-4 hover:prose-a:text-brand-500 hover:prose-a:decoration-brand-500 prose-blockquote:border-brand-500 prose-blockquote:bg-brand-50/70 prose-blockquote:px-5 prose-blockquote:py-1 prose-blockquote:not-italic prose-li:marker:text-brand-500"
          />
        </div>
      </main>

      <aside class="lg:pt-2">
        <div class="rounded-lg border border-brand-100 bg-brand-50/70 p-5 lg:sticky lg:top-28">
          <p class="text-xs font-bold uppercase tracking-[0.14em] text-brand-700">
            Continue the pathway
          </p>
          <h2 class="mt-3 text-xl font-bold leading-tight text-brand-900">
            Put this insight into practice
          </h2>
          <ul class="mt-5 space-y-1 border-t border-brand-100 pt-3">
            <li
              v-for="link in pathwayLinks"
              :key="link.to"
            >
              <NuxtLink
                :to="link.to"
                class="group block rounded-md px-3 py-3 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                <span class="flex items-start justify-between gap-3 font-semibold text-brand-800">
                  {{ link.label }}
                  <UIcon
                    name="i-heroicons-arrow-up-right-20-solid"
                    class="mt-0.5 size-4 shrink-0 text-brand-600 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </span>
                <span class="mt-1 block text-sm leading-6 text-slate-600">
                  {{ link.description }}
                </span>
              </NuxtLink>
            </li>
          </ul>
          <NuxtLink
            to="/contact"
            class="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            Discuss your pathway
          </NuxtLink>
        </div>
      </aside>
    </div>
  </div>
</template>
